import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import { isEqual } from 'lodash';
import {
  changeActiveObjective,
  clearActiveObjective,
  clearModals,
  updateIsDiscardModalOpen,
  updateIsExplodeModalOpen,
  updateIsNextObjectiveModalOpen,
  updateIsObjectiveChange,
  updateNextObjective,
  updateObjectiveToCancel,
  updateObjectiveToExplode,
} from 'store/objective/reducer';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { changePlannerSecondaryPanelType } from 'store/dealManagement/reducer';
import { getIsProgrammaticEndedAddNewLineEnabled } from 'store/dealManagement/selectors/getIsProgrammaticEndedAddNewLineEnabled';
import usePrevious from 'customHooks/usePrevious';
import { Store } from 'components/common/types/Store.types';
import {
  DirectSalesLineObjective,
  DirectSalesObjectiveFilterType,
  DirectSalesObjectiveToRemove,
} from 'components/common/types/DirectSalesCampaign.types';
import { CodeNameModel } from 'components/common/types';
import { isOneFilterSelected, isOneTargetSelected } from 'utils/objectives';
import { getNewObjectiveId, getUniqueObjectiveName } from 'store/dealManagement/utils';
import { DEFAULT_CRITERIA } from 'consts/targetCriteria';
import { DEFAULT_OBJECTIVE } from 'consts/objective';
import { addToastNotification } from 'store/notification/reducer';
import useIsReadOnly from '../../useIsReadOnly';
import { useCheckDirectSalesMandatoryFields } from '../../hooks/useCheckDirectSalesMandatoryFields';
import useEditDirectSalesLine from '../../hooks/DirectSalesPlannerActions/useEditDirectSalesLine';
import { explodeFilters, LINE_OBJECTIVES_LIMIT, OBJECTIVE_EXPLODE_COUNT_WARNING } from './utils/explodeFilters';

interface UseObjectives {
  objectives: DirectSalesLineObjective[];
  initialObjectiveState?: DirectSalesLineObjective;
  confirmModal: (
    objective: DirectSalesObjectiveToRemove,
    functionCallBack: (objective: CodeNameModel | null) => void,
  ) => void;
  confirmDiscardChanges: VoidFunction;
  confirmObjectiveChange: VoidFunction;
  handleChangeActiveObjective: (objective: DirectSalesLineObjective) => void;
  handleClosePanel: VoidFunction;
  handleDuplicateObjective: (objective: DirectSalesLineObjective) => void;
  onFilterChange: (data: Partial<DirectSalesLineObjective>) => void;
  isObjectiveChange: boolean;
  isReadOnly: boolean;
  isMandatoryData: boolean;
  closeObjectivePanel: VoidFunction;
  openObjectivePanel: (objective: DirectSalesLineObjective) => void;
  explodeObjective: (
    objective: DirectSalesLineObjective,
    objectiveFilterType: DirectSalesObjectiveFilterType,
    functionCallBack: (explodedObjectives: DirectSalesLineObjective[]) => void,
  ) => void;
  handleExplodeObjectiveValidation: (
    objective: DirectSalesLineObjective,
    objectiveFilterType: DirectSalesObjectiveFilterType,
    functionCallBack: (explodedObjectives: DirectSalesLineObjective[]) => void,
  ) => void;
}

type UseObjectivesProps = {
  lineId?: string;
  lineObjectives: DirectSalesLineObjective[];
};

export const createDefaultObjective = (lineObjectives: DirectSalesLineObjective[]): DirectSalesLineObjective => {
  const newObjectiveId = getNewObjectiveId(lineObjectives);
  const newObjectiveName = getUniqueObjectiveName(lineObjectives, newObjectiveId);

  return {
    ...DEFAULT_OBJECTIVE,
    objectiveGroupId: newObjectiveId,
    name: newObjectiveName,
    budget: {
      ...DEFAULT_CRITERIA,
      objectiveId: null,
    },
    frames: {
      ...DEFAULT_CRITERIA,
      objectiveId: null,
    },
    impressions: {
      ...DEFAULT_CRITERIA,
      objectiveId: null,
    },
  };
};

export const createNewObjectiveFromExisting = (
  objective: DirectSalesLineObjective,
  lineObjectives: DirectSalesLineObjective[],
): {
  newObjective: DirectSalesLineObjective;
  existingObjectives: DirectSalesLineObjective[];
} => {
  const newObjectiveId = getNewObjectiveId(lineObjectives);
  const newObjectiveName = getUniqueObjectiveName(lineObjectives, newObjectiveId);

  const existingObjectives = lineObjectives.map((obj) => ({
    ...obj,
    objectiveStatus: undefined,
  }));

  const newObjective = {
    ...objective,
    objectiveGroupId: newObjectiveId,
    name: newObjectiveName,
    budget: {
      ...objective.budget,
      objectiveId: null,
    },
    frames: {
      ...objective.frames,
      objectiveId: null,
    },
    impressions: {
      ...objective.impressions,
      objectiveId: null,
    },
    objectiveStatus: undefined,
  };

  return {
    newObjective,
    existingObjectives,
  };
};

const useObjectives = ({ lineId = '', lineObjectives }: UseObjectivesProps): UseObjectives => {
  const dispatch = useDispatch();
  const isProgrammaticEndedAddNewLineEnabled = useSelector(getIsProgrammaticEndedAddNewLineEnabled);
  const { isLineReadOnly: isReadOnly } = useIsReadOnly({
    lineId,
    isEditingAllowed: isProgrammaticEndedAddNewLineEnabled,
  });
  const { areDealInfoMandatoryFieldsFilled: isMandatoryData } = useCheckDirectSalesMandatoryFields(lineId);
  const editDealLine = useEditDirectSalesLine();

  const {
    data: activeObjective,
    data: { objectiveGroupId: activeObjectiveGroupId },
    settings: { isSubsectionChange, nextObjective },
  } = useSelector((state: Store) => state.objective);

  const plannerSecondaryPanelType = useSelector((state: Store) => state.dealManagement.plannerSecondaryPanelType);
  const previousPlannerSecondaryPanelType = usePrevious(plannerSecondaryPanelType);

  const openObjectivePanel = (objective: DirectSalesLineObjective): void => {
    dispatch(changeActiveObjective(objective));
    dispatch(changePlannerSecondaryPanelType(PlannerSecondaryPanelType.OBJECTIVE));
    dispatch(clearModals());
  };

  const closeObjectivePanel = (): void => {
    dispatch(changePlannerSecondaryPanelType(PlannerSecondaryPanelType.NONE));
    dispatch(clearActiveObjective());
    dispatch(clearModals());
  };

  const confirmDiscardChanges = (): void => {
    closeObjectivePanel();
    dispatch(updateIsDiscardModalOpen(false));
  };

  const onFilterChange = (data: Partial<DirectSalesLineObjective>): void => {
    dispatch(changeActiveObjective(data));
    dispatch(changePlannerSecondaryPanelType(PlannerSecondaryPanelType.OBJECTIVE));
  };

  const initialObjectiveState = useMemo(() => {
    return lineObjectives.find((objective) => objective.objectiveGroupId === activeObjectiveGroupId);
  }, [lineObjectives, activeObjectiveGroupId]);

  const isObjectiveChange = useMemo(
    () => Boolean(initialObjectiveState && activeObjectiveGroupId) && !isEqual(initialObjectiveState, activeObjective),
    [initialObjectiveState, activeObjective],
  );

  const isNewObjective = useMemo(
    () => !(isOneTargetSelected(initialObjectiveState) && isOneFilterSelected(initialObjectiveState)),
    [initialObjectiveState],
  );

  useEffect(() => {
    if (
      previousPlannerSecondaryPanelType?.includes(PlannerSecondaryPanelType.OBJECTIVE) &&
      !plannerSecondaryPanelType.includes(PlannerSecondaryPanelType.OBJECTIVE)
    ) {
      dispatch(clearActiveObjective());
    }
  }, [previousPlannerSecondaryPanelType, plannerSecondaryPanelType]);

  useEffect(() => {
    dispatch(updateIsObjectiveChange(isObjectiveChange));
  }, [isObjectiveChange]);

  useEffect(() => {
    return () => {
      dispatch(clearModals());
    };
  }, []);

  const handleChangeActiveObjective = useCallback(
    (objective: DirectSalesLineObjective) => {
      if (initialObjectiveState && isNewObjective) {
        dispatch(
          updateObjectiveToCancel({
            code: initialObjectiveState.objectiveGroupId.toString(),
            name: initialObjectiveState.name,
          }),
        );
      } else {
        if (isObjectiveChange || isSubsectionChange) {
          dispatch(updateNextObjective(activeObjectiveGroupId === objective.objectiveGroupId ? null : objective));
          dispatch(updateIsNextObjectiveModalOpen(true));
          return;
        }

        if (objective.objectiveGroupId !== activeObjectiveGroupId) openObjectivePanel(objective);
        else closeObjectivePanel();
      }
    },
    [activeObjective, activeObjectiveGroupId, isNewObjective, isObjectiveChange, isSubsectionChange],
  );

  const handleDuplicateObjective = useCallback(
    async (objective: DirectSalesLineObjective) => {
      const { newObjective, existingObjectives } = createNewObjectiveFromExisting(objective, lineObjectives);

      await editDealLine({ lineId, directSalesData: { objectives: [...existingObjectives, newObjective] } });
    },
    [lineObjectives, lineId, editDealLine],
  );

  const confirmObjectiveChange = useCallback(() => {
    if (nextObjective) openObjectivePanel(nextObjective);
    else closeObjectivePanel();
  }, [nextObjective]);

  const confirmModal = useCallback(
    async (objective: DirectSalesObjectiveToRemove, functionCallBack: (objective: CodeNameModel | null) => void) => {
      if (!objective) return;

      const objectiveId = Number(objective.code);

      if (objectiveId === activeObjectiveGroupId) closeObjectivePanel();

      const newObjectives = lineObjectives
        .filter((obj) => obj.objectiveGroupId !== Number(objective.code))
        .map((obj) => ({ ...obj, objectiveStatus: undefined }));

      await editDealLine({
        lineId,
        directSalesData: { objectives: newObjectives },
      });
      functionCallBack(null);
    },
    [lineObjectives, activeObjectiveGroupId, lineId, editDealLine],
  );

  const explodeObjective = useCallback(
    (
      objective: DirectSalesLineObjective,
      objectiveFilterType: DirectSalesObjectiveFilterType,
      functionCallBack: (explodedObjectives: DirectSalesLineObjective[]) => void,
    ) => {
      const objectiveIndex = lineObjectives.findIndex(
        ({ objectiveGroupId }) => objectiveGroupId === objective.objectiveGroupId,
      );

      const { baseExplodedObjective, explodedObjectives } = explodeFilters({
        objective,
        objectiveFilterType,
        objectivesCount: lineObjectives[lineObjectives.length - 1].objectiveGroupId,
      });

      if (!baseExplodedObjective) return;

      const newObjectives = [
        ...lineObjectives.slice(0, objectiveIndex),
        baseExplodedObjective,
        ...lineObjectives.slice(objectiveIndex + 1),
        ...explodedObjectives,
      ];

      functionCallBack(newObjectives);
    },
    [lineObjectives],
  );

  const handleExplodeObjectiveValidation = useCallback(
    (
      objective: DirectSalesLineObjective,
      objectiveFilterType: DirectSalesObjectiveFilterType,
      functionCallBack: (explodedObjectives: DirectSalesLineObjective[]) => void,
    ) => {
      dispatch(updateObjectiveToExplode({ objective, objectiveFilterType }));

      const objectiveFiltersToExplodeCount =
        objectiveFilterType === 'frameList'
          ? objective[objectiveFilterType].listFiles.length + objective[objectiveFilterType].routeFrameCodes.length
          : objective[objectiveFilterType].length;

      const allObjectivesCount = lineObjectives.length + objectiveFiltersToExplodeCount - 1;

      if (objectiveFiltersToExplodeCount > OBJECTIVE_EXPLODE_COUNT_WARNING) {
        dispatch(updateIsExplodeModalOpen(true));

        return;
      }

      if (allObjectivesCount > LINE_OBJECTIVES_LIMIT) {
        dispatch(
          addToastNotification({
            description: `Deal lines cannot exceed ${LINE_OBJECTIVES_LIMIT} Objectives`,
            timeout: 10000,
            type: 'Warning',
          }),
        );

        dispatch(updateObjectiveToExplode({ objective: null, objectiveFilterType: null }));

        return;
      }

      explodeObjective(objective, objectiveFilterType, functionCallBack);
    },
    [lineObjectives, explodeObjective],
  );

  const handleClosePanel = useCallback(() => {
    if (
      initialObjectiveState &&
      !(isOneTargetSelected(initialObjectiveState) && isOneFilterSelected(initialObjectiveState))
    ) {
      dispatch(
        updateObjectiveToCancel({
          code: initialObjectiveState.objectiveGroupId.toString(),
          name: initialObjectiveState.name,
          lineId,
        }),
      );
      return;
    }

    if (isObjectiveChange || isSubsectionChange) {
      dispatch(updateIsDiscardModalOpen(true));
      return;
    }

    closeObjectivePanel();
  }, [isObjectiveChange, isSubsectionChange, initialObjectiveState, lineId]);

  return {
    objectives: lineObjectives,
    initialObjectiveState,
    confirmModal,
    confirmDiscardChanges,
    confirmObjectiveChange,
    handleChangeActiveObjective,
    handleClosePanel,
    handleDuplicateObjective,
    onFilterChange,
    isObjectiveChange,
    isReadOnly,
    isMandatoryData,
    closeObjectivePanel,
    openObjectivePanel,
    explodeObjective,
    handleExplodeObjectiveValidation,
  };
};

export default useObjectives;
